import { z } from 'zod/v4-mini';
import { mutationProcedure } from '../trpc/common.js';
import { addOverride } from '../utilities/addOverride.js';
import type { OverrideOptions } from '../types/OverrideOptions.js';
import type { ApiParams } from '../types/ApiParams.js';
import { errorEntrySource } from '../data/busSources.js';
import { matchingZodObject } from '../utilities/matchingZodObject.js';
import { notifyHost } from '../utilities/notifyHost.js';

const ZodAddPackageOverrideInput = matchingZodObject<OverrideOptions>({
  packageName: z.string(),
  importPath: z.string(),
  version: z.optional(z.string()),
  issuerUrl: z.optional(z.string()),
  initiatedBy: z.literal(['autoUpdateEntries', 'user']),
});

// TODO: consider whether this should be hardcoded or not
const targetEnvironment = 'browser';

/**
 * Adds an override to the Cloudpack config.
 */
export async function addPackageOverride(
  params: ApiParams<
    OverrideOptions,
    'bus' | 'packageHashes' | 'packages' | 'taskRunner' | 'telemetryClient',
    'config' | 'incrementTargetVersion' | 'resolveMap' | 'sequence' | 'importMap' | 'packageImportPaths'
  >,
): Promise<boolean> {
  const { input, ctx } = params;
  const { session, bus, packages, taskRunner, packageHashes, telemetryClient } = ctx;
  const { packageName, initiatedBy } = input;

  return telemetryClient.tracer.startActiveSpan(
    'ADD_PACKAGE_OVERRIDE',
    { attributes: { packageName, initiatedBy } },
    async () => {
      const { addedOverride, entry } = await addOverride(input, { session, packages });
      if (entry && addedOverride) {
        const packagePath = entry.path;
        // The import map hash needs to be recalculated to ensure that the client is up to date.
        await packageHashes.get({
          packagePath,
          isSourceHashingEnabled: false,
          shouldRecalculate: true,
          targetEnvironment,
        });
        await taskRunner.clearResult(packagePath);
        // Incrementing the target version will cause the import map to be recalculated.
        session.incrementTargetVersion(packagePath);
        if (session.config.features?.autoUpdateEntries) {
          // We can clear the error message now that the override has been added.
          bus.publish(errorEntrySource, '');
        }
        await notifyHost(
          { reason: 'add-package-override', packageEntry: entry, definition: await packages.get(packagePath) },
          { bus, session, telemetryClient },
        );
      }
      return addedOverride;
    },
  );
}

addPackageOverride.procedure = mutationProcedure<OverrideOptions, boolean>(
  addPackageOverride,
  ZodAddPackageOverrideInput,
  z.boolean(),
);
